<template>
  <v-card class="resolution-card" rounded="xl" elevation="0">
    <v-card-item>
      <div class="resolution-card__head">
        <div>
          <div class="resolution-card__title">{{ conflict.message }}</div>
        </div>
        <div class="resolution-card__meta">
          <v-chip color="primary" variant="tonal" size="small">
            {{ conflict.code }}
          </v-chip>
          <v-chip color="amber-darken-2" variant="tonal" size="small">
            {{ affectedCountLabel }}
          </v-chip>
        </div>
      </div>
    </v-card-item>

    <v-card-text class="pt-0">
      <div v-if="hasSelectOptions" class="mb-5">
        <div class="resolution-card__section-title">Сопоставление</div>
        <v-autocomplete
          :items="selectOptions"
          item-title="label"
          item-value="__key"
          :model-value="selectedOptionKey"
          label="Выберите группу"
          variant="outlined"
          density="comfortable"
          clearable
          @update:model-value="onSelectCandidate"
        />
      </div>

      <div v-if="otherOptions.length" class="mb-4">
        <v-row>
          <v-col
            v-for="option in otherOptions"
            :key="option.__key"
            cols="12"
            md="6"
          >
            <v-sheet
                  class="resolution-card__action"
                  :class="{ 'resolution-card__action--selected': selectedOptionKey === option.__key }"
                  rounded="xl"
                  border
                  @click="onToggleAction(option.__key)"
            >
              <div class="resolution-card__action-label">
                {{ option.label }}
              </div>
              <div class="resolution-card__action-caption">
                {{ actionCaption(option) }}
              </div>
            </v-sheet>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "ConflictResolutionCard",
  props: {
    conflict: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedOptionKey: this.buildOptionKey(this.modelValue),
    };
  },
  computed: {
    normalizedOptions() {
      return this.conflict.options.map((option) => ({
        ...option,
        __key: this.buildOptionKey(option),
      }));
    },
    selectOptions() {
      return this.normalizedOptions.filter((option) => option.action === "select");
    },
    otherOptions() {
      return this.normalizedOptions.filter((option) => option.action !== "select");
    },
    hasSelectOptions() {
      return this.selectOptions.length > 0;
    },
    selectedOption() {
      return this.normalizedOptions.find((option) => option.__key === this.selectedOptionKey) ?? null;
    },
    affectedCountLabel() {
      const count = Array.isArray(this.conflict.targetId) ? this.conflict.targetId.length : 1;
      return `Уроков: ${count}`;
    },
  },
  watch: {
    modelValue: {
      deep: true,
      handler(value) {
        this.selectedOptionKey = this.buildOptionKey(value);
      },
    },
  },
  methods: {
    onSelectCandidate(value) {
      this.selectedOptionKey = value || null;
      this.emitChange();
    },
    onToggleAction(value) {
      this.selectedOptionKey = this.selectedOptionKey === value ? null : value;
      this.emitChange();
    },
    emitChange() {
      const option = this.selectedOption;

      if (!option) {
        this.$emit("update:modelValue", null);
        return;
      }

      this.$emit("update:modelValue", {
        code: this.conflict.code,
        targetId: this.conflict.targetId,
        action: option.action,
        label: option.label,
        payload: option.payload ?? null,
      });
    },
    buildOptionKey(value) {
      if (!value?.action) {
        return null;
      }

      return JSON.stringify({
        action: value.action,
        payload: value.payload ?? null,
      });
    },
    actionCaption(option) {
      if (option.action === "use_import") {
        return "Оставить уроки из текущего файла импорта и показывать их во внешнем расписании";
      }

      if (option.action === "skip" && option.payload?.mode === "skip_group") {
        return "Исключить эти уроки из текущего импорта";
      }

      if (option.action === "skip") {
        return "Не импортировать эти уроки";
      }

      return "Выбрать конкретную сущность для сопоставления";
    },
  },
};
</script>

<style scoped>
.resolution-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  background:
    radial-gradient(circle at top right, rgba(14, 165, 233, 0.08), transparent 32%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.resolution-card__head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.resolution-card__eyebrow {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin-bottom: 6px;
}

.resolution-card__title {
  font-size: 18px;
  line-height: 1.35;
  font-weight: 700;
  color: #0f172a;
}

.resolution-card__meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.resolution-card__section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
  margin-bottom: 12px;
}

.resolution-card__action {
  padding: 16px;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
  min-height: 92px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.86);
}

.resolution-card__action:hover {
  transform: translateY(-1px);
  border-color: rgba(30, 64, 175, 0.4);
}

.resolution-card__action--selected {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(59, 130, 246, 0.08);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
}

.resolution-card__action-label {
  font-weight: 700;
  color: #0f172a;
}

.resolution-card__action-caption {
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
}

@media (max-width: 960px) {
  .resolution-card__head {
    flex-direction: column;
  }

  .resolution-card__meta {
    justify-content: flex-start;
  }
}
</style>
